import {
  ACQUISITION_SCHEDULER_MODE_VALUES,
  type SftpAcquisitionPollingConfig,
} from '@steerfi/shared/types/jsonb/integration-profile.types';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { FileFormatPicker } from './file-format-picker';

interface SftpAcquisitionFieldsProps {
  value: SftpAcquisitionPollingConfig | undefined;
  onChange: (v: SftpAcquisitionPollingConfig) => void;
  partyId?: string;
  disabled?: boolean;
}

const emptyConfig = (): SftpAcquisitionPollingConfig => ({
  enabled: false,
  secretArn: '',
  schedulerMode: ACQUISITION_SCHEDULER_MODE_VALUES[0],
  rules: [],
});

const isValidCron = (expr: string): boolean => {
  const parts = expr.trim().split(/\s+/);
  return parts.length === 5 && parts.every((p) => /^[\d,\-/*]+$/.test(p));
};

export const SftpAcquisitionFields: React.FC<SftpAcquisitionFieldsProps> = ({
  value,
  onChange,
  partyId,
  disabled,
}) => {
  const cfg = value ?? emptyConfig();
  const isDisabled = disabled ?? false;
  const [cronError, setCronError] = useState<string | null>(null);
  const [timezoneError, setTimezoneError] = useState<string | null>(null);

  useEffect(() => {
    setCronError(null);
  }, [cfg.scheduleCron]);

  useEffect(() => {
    setTimezoneError(null);
  }, [cfg.timezone]);

  const acquisitionEnabled = cfg.enabled !== false;
  const fieldsDisabled = isDisabled || !acquisitionEnabled;

  const setField = <K extends keyof SftpAcquisitionPollingConfig>(
    key: K,
    val: SftpAcquisitionPollingConfig[K]
  ) => {
    onChange({ ...cfg, [key]: val });
  };

  const deleteField = <K extends keyof SftpAcquisitionPollingConfig>(key: K) => {
    const next = { ...cfg };
    delete next[key];
    onChange(next);
  };

  return (
    <div className="space-y-4 rounded-xl border p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-foreground">Acquisition (Inbound)</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            How files (statements, returns) are pulled from the provider.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Switch
            id="acq-enabled"
            disabled={isDisabled}
            checked={acquisitionEnabled}
            onCheckedChange={(checked: boolean) => setField('enabled', checked)}
          />
          <Label htmlFor="acq-enabled" className="text-xs cursor-pointer">
            Enabled
          </Label>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="acq-arn" className="text-xs">
          Secret reference <span className="text-destructive">*</span>
        </Label>
        <Input
          id="acq-arn"
          className="text-xs font-mono"
          disabled={fieldsDisabled}
          placeholder="arn:aws:secretsmanager:us-east-1:..."
          value={cfg.secretArn}
          onChange={(e) => setField('secretArn', e.target.value)}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-xs">Scheduler mode</Label>
          <Select
            disabled={fieldsDisabled}
            value={cfg.schedulerMode ?? ACQUISITION_SCHEDULER_MODE_VALUES[0]}
            onValueChange={(v) =>
              setField('schedulerMode', v as (typeof ACQUISITION_SCHEDULER_MODE_VALUES)[number])
            }
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACQUISITION_SCHEDULER_MODE_VALUES.map((m) => (
                <SelectItem key={m} value={m} className="text-xs">
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="acq-cron" className="text-xs">
            Schedule (cron)
          </Label>
          <Input
            id="acq-cron"
            className="h-8 text-xs font-mono"
            disabled={fieldsDisabled}
            placeholder="0 */6 * * *"
            value={cfg.scheduleCron ?? ''}
            aria-invalid={cronError !== null}
            onChange={(e) => {
              setCronError(null);
              if (e.target.value) setField('scheduleCron', e.target.value);
              else deleteField('scheduleCron');
            }}
            onBlur={(e) => {
              const val = e.target.value;
              if (val && !isValidCron(val)) {
                setCronError('Must be a valid 5-field cron expression (e.g. 0 */6 * * *)');
              }
            }}
          />
          {cronError && <p className="text-xs text-destructive">{cronError}</p>}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="acq-timezone" className="text-xs">
          Timezone
        </Label>
        <Input
          id="acq-timezone"
          className="h-8 text-xs"
          disabled={fieldsDisabled}
          placeholder="America/New_York"
          value={cfg.timezone ?? ''}
          aria-invalid={timezoneError !== null}
          onChange={(e) => {
            setTimezoneError(null);
            if (e.target.value) setField('timezone', e.target.value);
            else deleteField('timezone');
          }}
          onBlur={(e) => {
            const val = e.target.value;
            if (val && val !== 'UTC' && !Intl.supportedValuesOf('timeZone').includes(val)) {
              setTimezoneError('Must be a valid IANA timezone (e.g. America/New_York, UTC)');
            }
          }}
        />
        {timezoneError && <p className="text-xs text-destructive">{timezoneError}</p>}
      </div>

      <FileFormatPicker
        value={cfg.rules}
        onChange={(rules) => setField('rules', rules)}
        {...(partyId ? { partyId } : {})}
        disabled={fieldsDisabled}
      />
    </div>
  );
};
